// ========================================
// PAGE À PROPOS - Version Premium
// Animations spectaculaires + Données Firebase
// ========================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import Navigation from '../../components/common/Navigation/Navigation';
import Footer from '../../components/common/Footer/Footer';
import ContactForm from '../../components/common/ContactForm/ContactForm';
import { useAboutPage, useSiteSettings } from '../../hooks';
import { 
  FiHeart, 
  FiUsers, 
  FiTarget, 
  FiStar, 
  FiBook, 
  FiGlobe,
  FiAward,
  FiCompass,
  FiDownload,
  FiMail,
  FiPhone,
  FiMapPin,
  FiLinkedin,
  FiTwitter,
  FiInstagram,
  FiFacebook,
  FiCalendar,
  FiBriefcase,
  FiChevronDown,
  FiCheck,
  FiCheckCircle,
  FiSend,
  FiZap,
  FiTrendingUp,
  FiUser,
  FiFeather,
  FiMessageCircle
} from 'react-icons/fi';
import './About.css';

// ============================================
// MAPPING DES ICÔNES
// ============================================
const iconMap = {
  heart: FiHeart,
  users: FiUsers,
  target: FiTarget,
  star: FiStar,
  book: FiBook,
  globe: FiGlobe,
  award: FiAward,
  compass: FiCompass,
  zap: FiZap,
  trending: FiTrendingUp,
  feather: FiFeather,
  briefcase: FiBriefcase,
  calendar: FiCalendar,
  user: FiUser
};

const getIcon = (iconName) => {
  const Icon = iconMap[iconName] || FiStar;
  return <Icon />;
};

// Icônes pour les highlights du hero
const highlightIcons = {
  creativity: FiZap,
  innovation: FiTrendingUp,
  excellence: FiAward
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
const About = () => {
  // ========== STATE ==========
  const [initialLoading, setInitialLoading] = useState(true);
  
  // ========== REFS ==========
  const heroRef = useRef(null);
  
  // ========== HOOKS FIREBASE ==========
  const { content, loading: aboutLoading } = useAboutPage();
  const { settings, loading: settingsLoading } = useSiteSettings();

  // ========== SCROLL ANIMATIONS ==========
  const { scrollYProgress: heroScrollProgress } = useScroll({
    offset: ["start start", "end start"]
  });

  const heroY = useTransform(heroScrollProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(heroScrollProgress, [0, 1], [1, 0.3]);

  // ========== LOADING EFFECT ==========
  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  // ========== SCROLL FUNCTION ==========
  const scrollToSection = useCallback((sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // ========== LOADING STATE ==========
  const isLoading = aboutLoading || settingsLoading || initialLoading;

  // ========== DONNÉES ==========
  const profile = content?.profile || {};
  const bio = content?.bio || '';
  const values = content?.values || [];
  const timeline = content?.timeline || [];
  const skills = content?.skills || [];
  const cvUrl = content?.cvUrl || settings?.cvUrl || null;
  const contact = content?.contact || {};
  const highlights = content?.highlights || [
    { icon: 'zap', label: 'Créativité' },
    { icon: 'trending', label: 'Innovation' },
    { icon: 'award', label: 'Excellence' }
  ];

  // Données de contact (fallback sur settings)
  const email = contact.email || settings?.email || '';
  const phone = contact.phone || settings?.phones?.[0] || '';
  const location = contact.location || settings?.address || 'Ouagadougou, Burkina Faso';
  
  // Liens sociaux
  const socialLinks = settings?.socialLinks || {};

  // Nom et initiales
  const name = profile.name || settings?.siteName || 'Gustave KONATE';
  const firstName = name.split(' ')[0];
  const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const title = profile.title || 'Communicant • Écrivain • Leader Associatif';
  const shortBio = profile.shortBio || "Passionné par la communication et l'écriture, je m'engage à créer des contenus qui inspirent et des projets qui transforment.";

  // Stats (si disponibles dans Firebase)
  const stats = content?.stats || [
    { number: '4+', label: 'Années d\'expérience' },
    { number: '50+', label: 'Projets réalisés' },
    { number: '100%', label: 'Engagement' }
  ];

  // ============================================
  // VARIANTS D'ANIMATION
  // ============================================
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", damping: 20, stiffness: 100 }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  // ============================================
  // LOADING SCREEN SPECTACULAIRE
  // ============================================
  if (isLoading) {
    return (
      <div className="about-page">
        <div className="loading-screen">
          <motion.div 
            className="loading-avatar"
            animate={{ 
              scale: [0.8, 1, 0.8], 
              borderRadius: ["40%", "50%", "40%"],
              boxShadow: [
                "0 0 0 0 var(--primary-color-alpha)",
                "0 0 0 20px transparent",
                "0 0 0 0 var(--primary-color-alpha)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {profile.image ? (
              <img src={profile.image} alt={name} />
            ) : (
              <span>{initials}</span>
            )}
          </motion.div>
          <motion.div className="loading-particles">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="loading-particle"
                animate={{
                  y: [-20, 20, -20],
                  x: [Math.sin(i) * 30, Math.cos(i) * 30, Math.sin(i) * 30],
                  opacity: [0.3, 1, 0.3]
                }}
                transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }}
              />
            ))}
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="loading-text"
          >
            Chargement du profil...
          </motion.p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDU PRINCIPAL
  // ============================================
  return (
    <div className="about-page">
      <Navigation />

      {/* ========== HERO SECTION ========== */}
      <section className="about-hero" ref={heroRef}>
        <motion.div className="hero-background" style={{ y: heroY }}>
          {/* Particules animées */}
          <div className="particle-network">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
            <div className="particle particle-5"></div>
            <div className="particle particle-6"></div>
            <div className="orbit orbit-1"></div>
            <div className="orbit orbit-2"></div>
            <div className="orbit orbit-3"></div>
          </div>
          <div className="hero-gradient"></div>
        </motion.div>
        
        <motion.div className="hero-content" style={{ opacity: heroOpacity }}>
          <div className="about-container">
            <motion.div
              className="profile-section"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Image de profil */}
              <motion.div className="profile-image-wrapper" variants={itemVariants}>
                <div className="image-outline"></div>
                <div className="image-glow"></div>
                <div className="image-container">
                  {profile.image ? (
                    <img
                      src={profile.image}
                      alt={name}
                      className="profile-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="profile-placeholder" style={{ display: profile.image ? 'none' : 'flex' }}>
                    <span>{initials}</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Informations du profil */}
              <motion.div className="profile-info" variants={itemVariants}>
                <motion.div 
                  className="profile-badge"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <FiAward />
                  <span>Profil Vérifié</span>
                </motion.div>

                <motion.h1 
                  className="profile-name"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {name}
                </motion.h1>
                
                <motion.div 
                  className="profile-title"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                >
                  <span>{title}</span>
                </motion.div>

                <motion.div 
                  className="profile-location"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                >
                  <FiMapPin />
                  <span>{location}</span>
                </motion.div>
                
                <motion.p 
                  className="profile-intro"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  {shortBio}
                </motion.p>
                
                {/* Highlights */}
                <motion.div 
                  className="profile-highlights"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1 }}
                >
                  {highlights.map((highlight, index) => {
                    const IconComponent = iconMap[highlight.icon] || FiStar;
                    return (
                      <motion.div 
                        key={index}
                        className="highlight"
                        whileHover={{ scale: 1.05, y: -5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <div className="highlight-icon"><IconComponent /></div>
                        <div className="highlight-text">{highlight.label}</div>
                      </motion.div>
                    );
                  })}
                </motion.div>
                
                {/* CTAs */}
                <motion.div 
                  className="profile-cta"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                >
                  <motion.button 
                    className="cta-primary"
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 40px var(--primary-color-alpha)" }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => scrollToSection('#contact')}
                  >
                    <FiMail />
                    <span>Me contacter</span>
                  </motion.button>
                  
                  {cvUrl && (
                    <motion.a 
                      href={cvUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cta-secondary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      download
                    >
                      <FiDownload />
                      <span>Télécharger CV</span>
                    </motion.a>
                  )}
                  
                  <motion.button 
                    className="cta-outline"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => scrollToSection('#parcours')}
                  >
                    <span>Mon parcours</span>
                    <FiChevronDown />
                  </motion.button>
                </motion.div>

                {/* Social Links */}
                {Object.keys(socialLinks).length > 0 && (
                  <motion.div 
                    className="profile-socials"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.4 }}
                  >
                    {socialLinks.linkedin && (
                      <motion.a 
                        href={socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, y: -3 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiLinkedin />
                      </motion.a>
                    )}
                    {socialLinks.twitter && (
                      <motion.a 
                        href={socialLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, y: -3 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiTwitter />
                      </motion.a>
                    )}
                    {socialLinks.facebook && (
                      <motion.a 
                        href={socialLinks.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, y: -3 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiFacebook />
                      </motion.a>
                    )}
                    {socialLinks.instagram && (
                      <motion.a 
                        href={socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, y: -3 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiInstagram />
                      </motion.a>
                    )}
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
        
        {/* Scroll Indicator */}
        <motion.div 
          className="scroll-hint"
          animate={{ y: [0, -10, 0], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
          onClick={() => scrollToSection('#bio')}
        >
          <FiChevronDown />
          <span>Découvrir mon profil</span>
        </motion.div>
      </section>

      {/* ========== BIO SECTION ========== */}
      {bio && (
        <section id="bio" className="about-bio">
          <div className="about-container">
            <motion.div 
              className="bio-content"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <motion.div className="section-header">
                <span className="section-label">Mon Histoire</span>
                <h2 className="section-title">
                  À Propos de <span className="title-accent">Moi</span>
                </h2>
              </motion.div>
              
              <div className="bio-layout">
                <motion.div 
                  className="bio-text"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div 
                    className="bio-paragraph"
                    dangerouslySetInnerHTML={{ __html: bio }}
                  />
                  <div className="bio-signature">
                    <div className="signature-line"></div>
                    <span>— {firstName}</span>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="bio-stats"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {stats.map((stat, index) => (
                    <motion.div 
                      key={index} 
                      className="stat-card"
                      whileHover={{ scale: 1.05, y: -5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <motion.div 
                        className="stat-number"
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.6 + index * 0.1, type: "spring" }}
                      >
                        {stat.number}
                      </motion.div>
                      <div className="stat-label">{stat.label}</div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ========== VALUES SECTION ========== */}
      {values.length > 0 && (
        <section className="about-values">
          <div className="about-container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Ce qui me guide</span>
              <h2 className="section-title">
                Mes <span className="title-accent">Valeurs</span>
              </h2>
              <p className="section-subtitle">
                Les principes fondamentaux qui orientent mon travail et mes engagements.
              </p>
            </motion.div>
            
            <div className="values-grid">
              {values.map((value, index) => (
                <motion.div 
                  key={value.id || index}
                  className="value-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                >
                  <div className="value-icon">
                    {getIcon(value.icon)}
                  </div>
                  <h3 className="value-title">{value.title}</h3>
                  <p className="value-description">{value.description}</p>
                  <div className="value-number">{String(index + 1).padStart(2, '0')}</div>
                  <div className="value-glow"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== TIMELINE/JOURNEY SECTION ========== */}
      {timeline.length > 0 && (
        <section id="parcours" className="about-journey">
          <div className="about-container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Mon chemin</span>
              <h2 className="section-title">
                Mon <span className="title-accent">Parcours</span>
              </h2>
              <p className="section-subtitle">
                Les étapes clés qui ont façonné mon expertise et ma vision.
              </p>
            </motion.div>
            
            <div className="journey-timeline">
              <div className="timeline-line">
                <motion.div 
                  className="timeline-progress"
                  initial={{ height: 0 }}
                  whileInView={{ height: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                />
              </div>
              
              {timeline.map((step, index) => {
                const IconComponent = step.type === 'education' ? FiBook : 
                                      step.type === 'milestone' ? FiAward : 
                                      FiBriefcase;
                return (
                  <motion.div 
                    key={step.id || index}
                    className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                  >
                    <motion.div 
                      className="timeline-content"
                      whileHover={{ scale: 1.02 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className="timeline-icon">
                        <IconComponent />
                      </div>
                      <div className="timeline-date">
                        <FiCalendar />
                        <span>{step.year}</span>
                      </div>
                      <h3 className="timeline-title">{step.role}</h3>
                      <p className="timeline-organization">{step.organization}</p>
                      <p className="timeline-description">{step.description}</p>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ========== SKILLS SECTION ========== */}
      {skills.length > 0 && (
        <section id="skills" className="about-skills">
          <div className="about-container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Mon expertise</span>
              <h2 className="section-title">
                Mes <span className="title-accent">Compétences</span>
              </h2>
              <p className="section-subtitle">
                Les domaines dans lesquels je peux vous accompagner efficacement.
              </p>
            </motion.div>
            
            <div className="skills-grid">
              {skills.map((skill, index) => (
                <motion.div 
                  key={skill.id || index}
                  className="skill-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="skill-header">
                    <span className="skill-name">{skill.name}</span>
                    <span className="skill-percentage">{skill.percentage}%</span>
                  </div>
                  
                  <div className="skill-bar">
                    <motion.div 
                      className="skill-progress"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${skill.percentage}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.5, delay: 0.3, ease: "easeOut" }}
                    >
                      <div className="progress-glow"></div>
                      <div className="progress-shine"></div>
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== CONTACT SECTION ========== */}
      <section id="contact" className="about-contact">
        <div className="about-container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label">Contact</span>
            <h2 className="section-title">
              Travaillons <span className="title-accent">Ensemble</span>
            </h2>
            <p className="section-subtitle">
              Vous avez un projet en tête ? N'hésitez pas à me contacter.
              Je serais ravi de discuter de la façon dont je peux vous aider.
            </p>
          </motion.div>
          
          <div className="contact-layout">
            {/* Contact Info */}
            <motion.div 
              className="contact-info"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="contact-heading">
                <FiMessageCircle />
                Parlons de votre projet
              </h3>
              <p className="contact-description">
                Je suis disponible pour échanger sur vos projets de communication,
                vos besoins rédactionnels ou vos initiatives associatives.
              </p>

              {/* CV Download */}
              {cvUrl && (
                <motion.a 
                  href={cvUrl}
                  className="cv-download-btn"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02, y: -3 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="cv-icon">
                    <FiDownload />
                  </div>
                  <div className="cv-text">
                    <span className="cv-label">Curriculum Vitae</span>
                    <span className="cv-action">Télécharger mon CV</span>
                  </div>
                </motion.a>
              )}
              
              {/* Contact Methods */}
              <div className="contact-methods">
                {email && (
                  <motion.a 
                    href={`mailto:${email}`}
                    className="contact-method"
                    whileHover={{ x: 5 }}
                  >
                    <div className="method-icon"><FiMail /></div>
                    <div className="method-details">
                      <span className="method-label">Email</span>
                      <span className="method-value">{email}</span>
                    </div>
                  </motion.a>
                )}
                
                {phone && (
                  <motion.a 
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    className="contact-method"
                    whileHover={{ x: 5 }}
                  >
                    <div className="method-icon"><FiPhone /></div>
                    <div className="method-details">
                      <span className="method-label">Téléphone</span>
                      <span className="method-value">{phone}</span>
                    </div>
                  </motion.a>
                )}
                
                <div className="contact-method">
                  <div className="method-icon"><FiMapPin /></div>
                  <div className="method-details">
                    <span className="method-label">Localisation</span>
                    <span className="method-value">{location}</span>
                  </div>
                </div>
              </div>
              
              {/* Social Links */}
              {Object.keys(socialLinks).length > 0 && (
                <div className="social-section">
                  <h4 className="social-heading">Retrouvez-moi sur</h4>
                  <div className="social-buttons">
                    {socialLinks.linkedin && (
                      <motion.a 
                        href={socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-button linkedin"
                        whileHover={{ scale: 1.1, y: -5 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiLinkedin />
                      </motion.a>
                    )}
                    
                    {socialLinks.twitter && (
                      <motion.a 
                        href={socialLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-button twitter"
                        whileHover={{ scale: 1.1, y: -5 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiTwitter />
                      </motion.a>
                    )}
                    
                    {socialLinks.facebook && (
                      <motion.a 
                        href={socialLinks.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-button facebook"
                        whileHover={{ scale: 1.1, y: -5 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiFacebook />
                      </motion.a>
                    )}
                    
                    {socialLinks.instagram && (
                      <motion.a 
                        href={socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-button instagram"
                        whileHover={{ scale: 1.1, y: -5 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiInstagram />
                      </motion.a>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
            
            {/* Contact Form */}
            <motion.div 
              className="contact-form-container"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="contact-form-card">
                <div className="form-header">
                  <FiSend />
                  <h3>Envoyez-moi un message</h3>
                  <p>Je vous répondrai dans les plus brefs délais</p>
                </div>
                <ContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;